/**
 * Reusable profile/entity fields: avatar URL, description, phone, address, map link.
 * Used for users, groups, apartments, tournaments, venues.
 * Creator/admin can edit; users edit their own profile only (handled by parent).
 */

import React from 'react';
import CachedAvatar from './CachedAvatar';
import './EntityProfileFields.css';

const DEFAULT_VALUE = {
  avatarURL: '',
  description: '',
  phone: '',
  address: '',
  mapUrl: ''
};

export function getEntityProfileDefaults(entity) {
  if (!entity) return { ...DEFAULT_VALUE };
  return {
    avatarURL: entity.avatarURL || entity.photoURL || '',
    description: entity.description || '',
    phone: entity.phone || '',
    address: entity.address || '',
    mapUrl: entity.mapUrl || ''
  };
}

export default function EntityProfileFields({
  value = {},
  onChange,
  showAvatar = true,
  showDescription = true,
  showPhone = true,
  showAddress = true,
  showMapUrl = true,
  disabled = false,
  avatarAlt = 'Avatar'
}) {
  const v = { ...DEFAULT_VALUE, ...value };

  const handleChange = (key) => (e) => {
    if (onChange) onChange(key, e.target.value);
  };

  return (
    <div className="entity-profile-fields">
      {showAvatar && (
        <div className="entity-profile-field entity-profile-avatar-row">
          <label>Profile picture / avatar</label>
          <div className="entity-profile-avatar-wrap">
            {v.avatarURL && (
              <CachedAvatar url={v.avatarURL} className="entity-profile-avatar-preview" alt={avatarAlt} />
            )}
            <input
              type="url"
              value={v.avatarURL || ''}
              onChange={handleChange('avatarURL')}
              placeholder="https://… (image URL)"
              disabled={disabled}
              className="entity-profile-input"
            />
          </div>
        </div>
      )}
      {showDescription && (
        <div className="entity-profile-field">
          <label>Description</label>
          <textarea
            value={v.description || ''}
            onChange={handleChange('description')}
            placeholder="Short description"
            rows={3}
            disabled={disabled}
            className="entity-profile-input entity-profile-textarea"
          />
        </div>
      )}
      {showPhone && (
        <div className="entity-profile-field">
          <label>Phone</label>
          <input
            type="tel"
            value={v.phone || ''}
            onChange={handleChange('phone')}
            placeholder="e.g. +91 98765 43210"
            disabled={disabled}
            className="entity-profile-input"
          />
        </div>
      )}
      {showAddress && (
        <div className="entity-profile-field">
          <label>Address</label>
          <input
            type="text"
            value={v.address || ''}
            onChange={handleChange('address')}
            placeholder="Street, city, PIN"
            disabled={disabled}
            className="entity-profile-input"
          />
        </div>
      )}
      {showMapUrl && (
        <div className="entity-profile-field">
          <label>Google Maps link (optional)</label>
          <input
            type="url"
            value={v.mapUrl || ''}
            onChange={handleChange('mapUrl')}
            placeholder="https://maps.google.com/… or embed URL"
            disabled={disabled}
            className="entity-profile-input"
          />
          <small className="entity-profile-hint">Paste a Google Maps share link so users can open the location.</small>
        </div>
      )}
    </div>
  );
}

/** Display-only block for entity cards / detail views */
export function EntityProfileDisplay({ entity, showAvatar = true, showDescription = true, showPhone = true, showAddress = true, showMapUrl = true }) {
  const v = getEntityProfileDefaults(entity);
  const hasAny = (showAvatar && v.avatarURL) || (showDescription && v.description) || (showPhone && v.phone) || (showAddress && v.address) || (showMapUrl && v.mapUrl);
  if (!hasAny) return null;
  return (
    <div className="entity-profile-display">
      {showAvatar && v.avatarURL && (
        <CachedAvatar url={v.avatarURL} className="entity-profile-avatar-preview" alt="" />
      )}
      {showDescription && v.description && (
        <p className="entity-profile-description">{v.description}</p>
      )}
      {showPhone && v.phone && (
        <p className="entity-profile-phone">📞 {v.phone}</p>
      )}
      {showAddress && v.address && (
        <p className="entity-profile-address">📍 {v.address}</p>
      )}
      {showMapUrl && v.mapUrl && (
        <p className="entity-profile-map-link">
          <a href={v.mapUrl} target="_blank" rel="noopener noreferrer">Open in Maps</a>
        </p>
      )}
    </div>
  );
}
